/* eslint-disable no-undef */

import { capturePostHogEvent } from './posthog';

/**
 * GA4 / GTM “engagement” helpers (Ticket 2).
 *
 * ── Step 1: `landing_variant` / `landing_variant_applied` ───────────────────
 *
 * What is the dataLayer?
 * - GTM injects a script that creates `window.dataLayer` (an array).
 * - Your site does `dataLayer.push({ ... })` with plain objects.
 * - In GTM you create triggers/tags that react to those pushes and send hits to GA4.
 * - You do NOT need to call `gtag()` yourself if GTM maps these events — that’s configured in GTM.
 *
 * What is `landing_variant_applied`?
 * - The analyst’s doc says: when we know if the visitor sees the UK-personalized homepage
 *   or the standard English site, we push so GTM/GA4 can store that segment.
 * - Allowed values (for now): `"uk"` | `"default"`.
 * - **Contract:** one logical “application” per **session** for a given resolved value — i.e. push
 *   when the homepage has **determined** `uk` vs `default`, not on every later return to `/`
 *   when that outcome is unchanged. (Earlier code only deduped within a single mount via a ref,
 *   so SPA revisits to home re-pushed the same event; that matched neither “once known” nor
 *   sessionStorage’s role and inflated GTM noise.)
 * - We skip a repeat push when `sessionStorage` already holds the same `landing_variant`;
 *   we push again if the stored value **differs** from the newly resolved one (segment change).
 *
 * Why sessionStorage too?
 * - The homepage knows the variant after geo runs. Other pages (pricing, blog, …) don’t
 *   re-run that logic. We copy the same string into sessionStorage so later code can call
 *   `getLandingVariant()` without scanning the whole dataLayer.
 *
 * ── Step 2: `pricing_page_intent` (Ticket §2.4) ───────────────────────────────
 * - Fires on **full client load** of `/pricing/` (see `src/pages/pricing.jsx`).
 * - Payload: `event: 'pricing_page_intent'`, `landing_variant` from `getLandingVariant()`
 *   (so it matches the homepage segment if the visitor hit `/` first this session).
 *
 * ── Step 3: `feature_page_view` (Ticket §2.3) ───────────────────────────────────
 * - Fires on **client load** of a **feature detail** URL: `/features/<slug>/` (not the hub `/features/`).
 * - Payload: `event: 'feature_page_view'`, `feature_name` (URL slug, e.g. `channel-manager`),
 *   plus `landing_variant` from `getLandingVariant()`.
 * - Implemented via `FeaturePageAnalytics` in `Common.jsx` so we don’t edit every feature page file.
 *
 * ── Step 4: `case_study_view` (Ticket §2.5) ───────────────────────────────────
 * - Fires on **client load** of `/case-studies/<slug>/` (see `templates/case-studies/item.jsx`).
 * - Payload: `event: 'case_study_view'`, `case_study_name` (Contentful slug, matches ticket),
 *   plus `landing_variant` from `getLandingVariant()`.
 *
 * ── Step 5: `blog_article_read` (Ticket §2.6) ──────────────────────────────────
 * - Fires **once per blog post view** when the user has scrolled **≥ 75%** of the page height.
 * - Payload: `event: 'blog_article_read'`, `article_title` (per ticket: `document.title`),
 *   plus `landing_variant` from `getLandingVariant()`.
 * - Implemented in `templates/blog/blog-post.jsx` (single post template only).
 *
 * ── Step 6: `nav_cta_click` (Ticket §2.1, PDF spec) ───────────────────────────
 * - When: user clicks any primary “Get started” or “Get pricing” (ticket wording).
 * - Payload **must** match the doc: `cta_text` (visible label), `cta_location` one of
 *   `"nav_bar"` | `"hero"` | `"body"` | `"footer"`, plus `landing_variant`.
 * - **`nav_bar`:** explicit `pushNavCtaClick` in `Navigation/index.jsx` (header sits outside `<Main>`).
 * - **In-page:** `installNavCtaClickDelegation` on `<Main>` in `Common.jsx` — any `<a href>` under
 *   main matching `linkMatchesTicket21NavCta` fires once; set `data-ga-cta-location` on a wrapper
 *   or anchor for `hero` / `body` / `footer`, otherwise location defaults to `body`.
 *
 * ── Step 7: `video_play` (Ticket §2.7) ────────────────────────────────────────
 * - When: user starts meaningful playback on `VideoPlayer` (skips passive muted preview loops
 *   until the visitor activates full playback where applicable).
 * - Payload: `event: 'video_play'`, `video_title`, `video_location` (page URL), `landing_variant`.
 * - Implemented in `components/VideoPlayer/video-player.jsx`. Raw `<iframe>` embeds elsewhere
 *   are out of scope until wired similarly.
 *
 * ── Step 8: `chatbot_open` (Ticket §2.8, PDF) ─────────────────────────────────
 * - When: user opens the Amy AI chat widget (Chili Piper / vendor).
 * - Payload: `event: 'chatbot_open'`, `landing_variant` from sessionStorage / dataLayer
 *   (same contract as other Ticket 2 events). **Implement in GTM** via Chili Piper
 *   `eventsHandler` + `dataLayer.push` (see `docs/Ticket-2-Navigation-and-Engagement-Datalayer-Events.pdf`).
 *
 * ── Step 9: `outbound_link_click` (Ticket §2.9, PDF) ────────────────────────────
 * - When: user clicks an `http(s)` link whose origin differs from `window.location.origin`.
 * - Payload: `event: 'outbound_link_click'`, `outbound_url` (full destination URL), `landing_variant`.
 * - **`installOutboundLinkClickDelegation(rootEl)`** on `<Wrapper>` in `Common.jsx` so nav, main,
 *   and footer are included. Skips `mailto:`, `tel:`, `javascript:`, and hash-only `href`.
 * - Opt out per anchor: `data-ga-outbound-delegate-ignore="true"`.
 */

export const LANDING_VARIANT_SESSION_KEY = 'hostaway_landing_variant';

/**
 * Ticket §2.1: URLs that count as primary “Get started” / “Get pricing” CTAs for `nav_cta_click`
 * when used from `body` (shared CTA components).
 */
export function linkMatchesTicket21NavCta(to) {
  if (!to || typeof to !== 'string') {
    return false;
  }
  const path = to.trim().split('?')[0].toLowerCase();
  if (path.includes('#get-quote')) {
    return true;
  }
  if (path.includes('get-free-demo')) {
    return true;
  }
  if (/\/pricing\/?$/.test(path) || path.includes('/pricing/')) {
    return true;
  }
  return false;
}

function readLandingVariantFromDataLayer() {
  const dl = window.dataLayer;
  if (!Array.isArray(dl)) {
    return null;
  }
  for (let i = dl.length - 1; i >= 0; i -= 1) {
    const row = dl[i];
    if (
      row &&
      row.event === 'landing_variant_applied' &&
      (row.landing_variant === 'uk' || row.landing_variant === 'default')
    ) {
      return row.landing_variant;
    }
  }
  return null;
}

/**
 * Value to use on engagement events (after step 1 is live).
 * Order: sessionStorage → last matching row in dataLayer → `"default"`.
 */
export function getLandingVariant() {
  if (typeof window === 'undefined') {
    return 'default';
  }
  try {
    const stored = sessionStorage.getItem(LANDING_VARIANT_SESSION_KEY);
    if (stored === 'uk' || stored === 'default') {
      return stored;
    }
  } catch {
    // storage blocked (private mode, etc.)
  }
  const fromDl = readLandingVariantFromDataLayer();
  if (fromDl === 'uk' || fromDl === 'default') {
    return fromDl;
  }
  return 'default';
}

/**
 * Step 1 / ticket: when the real homepage has resolved UK vs default, notify GTM once per
 * session for that outcome (see file header). Updates `sessionStorage` so `getLandingVariant()`
 * works on inner routes without re-scanning `dataLayer`.
 */
export function pushLandingVariantApplied(landingVariant) {
  if (typeof window === 'undefined') {
    return;
  }
  const variant =
    landingVariant === 'uk' || landingVariant === 'default'
      ? landingVariant
      : 'default';

  // One `landing_variant_applied` per session per resolved variant — avoids a new
  // dataLayer row on every SPA return to `/` when the segment did not change.
  try {
    const existing = sessionStorage.getItem(LANDING_VARIANT_SESSION_KEY);
    if (existing === variant) {
      return;
    }
  } catch {
    // storage blocked — still push below so GTM can see the segment at least once
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'landing_variant_applied',
    landing_variant: variant,
  });

  try {
    sessionStorage.setItem(LANDING_VARIANT_SESSION_KEY, variant);
  } catch {
    // ignore
  }
}

/**
 * Ticket §2.4: high-intent signal when someone opens the pricing page.
 * Must include `landing_variant` (same rules as the rest of Ticket 2).
 */
export function pushPricingPageIntent() {
  if (typeof window === 'undefined') {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'pricing_page_intent',
    landing_variant: getLandingVariant(),
  });
}

function stripTrailingSlash(p) {
  if (p.length > 1 && p.endsWith('/')) {
    return p.slice(0, -1);
  }
  return p;
}

/**
 * Strip Gatsby `assetPrefix` and locale prefix (`/fr/`, `/es-ES/`, `/it/`) from `location.pathname`
 * so we can match routes in a stable way.
 */
export function normalizeSitePath(pathname, pathPrefix = '') {
  if (!pathname) {
    return '/';
  }
  let p = pathname;
  if (pathPrefix && p.startsWith(pathPrefix)) {
    p = p.slice(pathPrefix.length) || '/';
  }
  if (!p.startsWith('/')) {
    p = `/${p}`;
  }
  const m = p.match(/^\/(fr|es-ES|it)(\/|$)/);
  if (m) {
    p = p.slice(m[0].length - 1) || '/';
    if (!p.startsWith('/')) {
      p = `/${p}`;
    }
  }
  return p;
}

/**
 * Returns the feature slug for `/features/<slug>/` detail pages, or `null` for `/features/` hub.
 */
export function getFeaturePageSlugFromPath(normalizedPath) {
  const p = stripTrailingSlash(normalizedPath);
  const m = p.match(/^\/features\/([^/]+)$/);
  if (!m) {
    return null;
  }
  const slug = m[1];
  if (!slug || slug === 'index') {
    return null;
  }
  return slug;
}

/**
 * Ticket §2.3: someone opened a specific feature marketing page (research intent).
 * `feature_name` uses the URL slug (matches the ticket’s example `channel-manager`).
 */
export function pushFeaturePageView(featureName) {
  if (typeof window === 'undefined' || !featureName) {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'feature_page_view',
    feature_name: String(featureName),
    landing_variant: getLandingVariant(),
  });
}

/**
 * Ticket §2.5: someone opened a case study detail page (social proof engagement).
 * `case_study_name` uses the URL / CMS slug (ticket example: `paradise-rentals`).
 */
export function pushCaseStudyView(caseStudyName) {
  if (typeof window === 'undefined' || !caseStudyName) {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'case_study_view',
    case_study_name: String(caseStudyName),
    landing_variant: getLandingVariant(),
  });
}

/**
 * Ticket §2.6: reader reached ≥75% scroll depth on a blog post (once per page view).
 * Ticket specifies `article_title` from `document.title`.
 */
export function pushBlogArticleRead(articleTitle) {
  if (typeof window === 'undefined' || !articleTitle) {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'blog_article_read',
    article_title: String(articleTitle).slice(0, 300),
    landing_variant: getLandingVariant(),
  });
}

/**
 * Ticket §2.7: user started playback on an embedded video (see `VideoPlayer`).
 * `video_location` defaults to the current page URL per PDF example.
 */
export function pushVideoPlay({ videoTitle, videoLocation }) {
  if (typeof window === 'undefined' || !videoTitle) {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'video_play',
    video_title: String(videoTitle).slice(0, 300),
    video_location:
      (videoLocation && String(videoLocation).slice(0, 2000)) ||
      window.location.href,
    landing_variant: getLandingVariant(),
  });
}

/** §2.1: allowlisted `cta_location` values (Ticket 2 PDF). */
const NAV_CTA_LOCATIONS = new Set(['nav_bar', 'hero', 'body', 'footer']);

/**
 * Ticket §2.1 (PDF): primary Get started / Get pricing clicks.
 *
 * @param {{ ctaText: string, ctaLocation: 'nav_bar' | 'hero' | 'body' | 'footer' }} params
 */
export function pushNavCtaClick({ ctaText, ctaLocation }) {
  if (typeof window === 'undefined') {
    return;
  }
  const text = typeof ctaText === 'string' ? ctaText.trim() : '';
  if (!text) {
    return;
  }
  if (!ctaLocation || !NAV_CTA_LOCATIONS.has(ctaLocation)) {
    return;
  }
  const landingVariant = getLandingVariant();
  const ctaTextSafe = text.slice(0, 120);
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'nav_cta_click',
    cta_text: ctaTextSafe,
    cta_location: ctaLocation,
    landing_variant: landingVariant,
  });

  // PostHog funnel: Get started / Get pricing CTA (same allowlist as GA4).
  capturePostHogEvent('demo_cta_clicked', {
    cta_text: ctaTextSafe,
    cta_location: ctaLocation,
    landing_variant: landingVariant,
  });
}

const DATA_GA_CTA_LOCATION = 'data-ga-cta-location';

/**
 * Walk from `anchor` up to `root` (exclusive) for `data-ga-cta-location` (PDF §2.1).
 * @returns {'nav_bar'|'hero'|'body'|'footer'|null}
 */
function readTicket21CtaLocationFromAncestors(anchor, root) {
  let node = anchor;
  while (node && node !== root) {
    const v = node.getAttribute?.(DATA_GA_CTA_LOCATION);
    if (v && NAV_CTA_LOCATIONS.has(v)) {
      return v;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * Delegated §2.1 tracking for demo/pricing links inside `rootEl` (typically `<Main>`).
 * Skips anchors with `data-ga-nav-cta-delegate-ignore="true"`.
 * @returns {() => void} cleanup
 */
export function installNavCtaClickDelegation(rootEl) {
  if (typeof window === 'undefined' || !rootEl) {
    return () => {};
  }

  const onClick = (event) => {
    let t = event.target;
    if (t && t.nodeType === Node.TEXT_NODE) {
      t = t.parentElement;
    }
    if (!t || typeof t.closest !== 'function') {
      return;
    }
    const anchor = t.closest('a[href]');
    if (!anchor || !rootEl.contains(anchor)) {
      return;
    }
    if (anchor.getAttribute('data-ga-nav-cta-delegate-ignore') === 'true') {
      return;
    }
    const href = anchor.getAttribute('href') || '';
    if (!linkMatchesTicket21NavCta(href)) {
      return;
    }
    const fromDom = readTicket21CtaLocationFromAncestors(anchor, rootEl);
    const ctaLocation = fromDom || 'body';
    const ctaText = (anchor.textContent || '').replace(/\s+/g, ' ').trim();
    if (!ctaText) {
      return;
    }
    pushNavCtaClick({ ctaText, ctaLocation });
  };

  rootEl.addEventListener('click', onClick);
  return () => {
    rootEl.removeEventListener('click', onClick);
  };
}

const DATA_GA_OUTBOUND_DELEGATE_IGNORE = 'data-ga-outbound-delegate-ignore';

/**
 * Ticket §2.9 (PDF): user followed a link to another origin (exit / partner traffic).
 *
 * @param {{ outboundUrl: string }} params
 */
export function pushOutboundLinkClick({ outboundUrl }) {
  if (typeof window === 'undefined' || !outboundUrl) {
    return;
  }
  const url = String(outboundUrl).trim();
  if (!url) {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'outbound_link_click',
    outbound_url: url.slice(0, 2000),
    landing_variant: getLandingVariant(),
  });
}

/**
 * Delegated §2.9 tracking: any `http(s)` `<a href>` under `rootEl` pointing off-site.
 * @returns {() => void} cleanup
 */
export function installOutboundLinkClickDelegation(rootEl) {
  if (typeof window === 'undefined' || !rootEl) {
    return () => {};
  }

  const onClick = (event) => {
    let t = event.target;
    if (t && t.nodeType === Node.TEXT_NODE) {
      t = t.parentElement;
    }
    if (!t || typeof t.closest !== 'function') {
      return;
    }
    const anchor = t.closest('a[href]');
    if (!anchor || !rootEl.contains(anchor)) {
      return;
    }
    if (anchor.getAttribute(DATA_GA_OUTBOUND_DELEGATE_IGNORE) === 'true') {
      return;
    }
    const hrefAttr = anchor.getAttribute('href') || '';
    if (!hrefAttr.trim() || hrefAttr.trim().startsWith('#')) {
      return;
    }
    let resolved;
    try {
      resolved = new URL(anchor.href);
    } catch {
      return;
    }
    if (resolved.protocol !== 'http:' && resolved.protocol !== 'https:') {
      return;
    }
    if (resolved.origin === window.location.origin) {
      return;
    }
    pushOutboundLinkClick({ outboundUrl: resolved.href });
  };

  rootEl.addEventListener('click', onClick);
  return () => {
    rootEl.removeEventListener('click', onClick);
  };
}

/**
 * Channel landing page (/vacation-rental-channel-manager/{name}/) view.
 */
export function pushChannelPageView(channelName) {
  if (typeof window === 'undefined') {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'channel_page_view',
    channel_name: String(channelName || '').slice(0, 120),
    landing_variant: getLandingVariant(),
  });
}

/**
 * /tools/ calculators — page view. Fired once on mount per tool page
 * (toolName: 'tools-hub' | 'airbnb-fee-calculator' | 'rental-arbitrage-calculator').
 */
export function pushToolPageView(toolName) {
  if (typeof window === 'undefined') {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'tool_page_view',
    tool_name: String(toolName).slice(0, 120),
    landing_variant: getLandingVariant(),
  });
}

/**
 * /tools/ calculators — a calculation produced results. The calling page owns
 * debouncing (~1200ms) and skipping the initial default-values render; this
 * helper is a plain push. `fields` must be flat, numeric/string only.
 */
export function pushToolCalculated({ toolName, ...fields }) {
  if (typeof window === 'undefined') {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'tool_calculated',
    tool_name: String(toolName).slice(0, 120),
    ...fields,
    landing_variant: getLandingVariant(),
  });
}

/**
 * /tools/ calculators — demo CTA click. Note: demo links inside <Main> also
 * fire nav_cta_click via the existing delegation; tool_cta_click adds
 * per-tool attribution on top (intentional).
 */
export function pushToolCtaClick({ toolName, ctaText }) {
  if (typeof window === 'undefined') {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'tool_cta_click',
    tool_name: String(toolName).slice(0, 120),
    cta_text: String(ctaText || '').slice(0, 120),
    landing_variant: getLandingVariant(),
  });
}

/**
 * /tools/ calculators — a share action. `method` is one of:
 * copy_link | copy_results | chatgpt | perplexity | native_share.
 */
export function pushToolShared({ toolName, method }) {
  if (typeof window === 'undefined') {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'tool_shared',
    tool_name: String(toolName).slice(0, 120),
    share_method: String(method || '').slice(0, 40),
    landing_variant: getLandingVariant(),
  });
}

/**
 * Glossary term page — click on the "Run the numbers" tool callout that
 * links a term to its matching /tools/ calculator.
 */
export function pushToolCalloutClick({ toolName, termSlug }) {
  if (typeof window === 'undefined') {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'tool_callout_click',
    tool_name: String(toolName).slice(0, 120),
    term_slug: String(termSlug || '').slice(0, 120),
    landing_variant: getLandingVariant(),
  });
}

/**
 * Guide checklist pages — interaction burst (debounced by the component).
 */
export function pushChecklistToggle({ slug, done, total }) {
  if (typeof window === 'undefined') {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'checklist_item_toggle',
    checklist_slug: String(slug || '').slice(0, 120),
    items_done: Number(done) || 0,
    items_total: Number(total) || 0,
    landing_variant: getLandingVariant(),
  });
}

/**
 * Guide checklist pages — print button.
 */
export function pushChecklistPrint(slug) {
  if (typeof window === 'undefined') {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'checklist_print',
    checklist_slug: String(slug || '').slice(0, 120),
    landing_variant: getLandingVariant(),
  });
}

/**
 * Guide index hub — debounced search event (debounce lives in the component).
 */
export function pushGuideIndexSearch({ query, resultCount }) {
  if (typeof window === 'undefined') {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'guide_index_search',
    search_query: String(query || '').slice(0, 120),
    result_count: Number(resultCount) || 0,
    landing_variant: getLandingVariant(),
  });
}

/**
 * Guide section conversion CTAs (Hostaway Helps band, checklist completion,
 * inline article bands). location distinguishes the surface.
 */
export function pushGuideCtaClick({ slug, location, ctaText }) {
  if (typeof window === 'undefined') {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'guide_cta_click',
    guide_slug: String(slug || '').slice(0, 120),
    cta_location: String(location || '').slice(0, 60),
    cta_text: String(ctaText || '').slice(0, 120),
    landing_variant: getLandingVariant(),
  });
}
